import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResizeDetector } from 'react-resize-detector';

import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { useEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import { DEFAULT_ZOOM } from '~/core/constants';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectRestorableColor,
  selectSelectedColor,
  updateSelectedColorId,
} from '~/redux/slices/color';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectCurrentEstimate } from '~/redux/slices/estimate';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import PageSubtitle from '~atoms/PageSubtitle';
import PageTitle from '~atoms/PageTitle';
import { useDrawingShapeContext } from '~contexts';
import DrawingArea from '~organisms/DrawingArea';
import Design from '~templates/Design';

const DesignPage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const selectedColor = useAppSelector(selectSelectedColor);
  const restorableColor = useAppSelector(selectRestorableColor);
  const currentEstimate = useAppSelector(selectCurrentEstimate);
  const canvas = useAppSelector(selectFabricCanvas);
  const {
    getters: { areThereShapesInTheDrawingArea, drawingShapes },
  } = useDrawingShapeContext();

  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const { width, ref } = useResizeDetector();

  // Force data fetching for design tabs even when color is not fully loaded
  // This ensures edges, corner radius, and diagonal corners are available
  const { sortedEdges, isFetchingEdges } = useEdges();
  const { cornerRadii, isFetchingCornerRadii } = useCornerRadii();
  const { diagonalCorners, isFetchingDiagonalCorners } = useDiagonalCorners();

  // Force canvas redraw when color becomes available and there are shapes to draw
  useEffect(() => {
    if (selectedColor?.overlayColor && areThereShapesInTheDrawingArea) {
      // Small delay to ensure all components are mounted and ready
      const timeoutId = setTimeout(() => {
        dispatch(updateNeedRedrawCanvas(true));
        setIsCanvasReady(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [selectedColor?.overlayColor, areThereShapesInTheDrawingArea, dispatch]);

  // Force canvas redraw when estimate is loaded but color is not yet available
  // This handles the case where user navigates directly to design page
  useEffect(() => {
    if (
      areThereShapesInTheDrawingArea &&
      !selectedColor?.overlayColor &&
      (restorableColor?.overlayColor || currentEstimate?.color?.overlayColor)
    ) {
      // Force color selection from restorable or current estimate
      const colorToRestore = restorableColor || currentEstimate?.color;

      if (colorToRestore?.productId) {
        dispatch(updateSelectedColorId(colorToRestore.productId));
      }

      // Force redraw with a longer delay to allow color restoration
      const timeoutId = setTimeout(() => {
        dispatch(updateNeedRedrawCanvas(true));
        setIsCanvasReady(true);
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [
    areThereShapesInTheDrawingArea,
    selectedColor?.overlayColor,
    restorableColor?.overlayColor,
    currentEstimate?.color?.overlayColor,
    dispatch,
    restorableColor,
    currentEstimate?.color,
  ]);

  // Additional trigger for canvas redraw when data loading is complete
  useEffect(() => {
    if (
      areThereShapesInTheDrawingArea &&
      !isFetchingEdges &&
      !isFetchingCornerRadii &&
      !isFetchingDiagonalCorners &&
      (sortedEdges.length > 0 ||
        cornerRadii.length > 0 ||
        diagonalCorners.length > 0) &&
      !isCanvasReady
    ) {
      const timeoutId = setTimeout(() => {
        dispatch(updateNeedRedrawCanvas(true));
        setIsCanvasReady(true);
      }, 200);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [
    areThereShapesInTheDrawingArea,
    isFetchingEdges,
    isFetchingCornerRadii,
    isFetchingDiagonalCorners,
    sortedEdges.length,
    cornerRadii.length,
    diagonalCorners.length,
    isCanvasReady,
    dispatch,
  ]);

  // Ensure canvas zoom is stable when shapes are present
  useEffect(() => {
    if (canvas && selectedColor && drawingShapes.length > 0) {
      const currentZoom = canvas.getZoom();

      // Fix any invalid zoom values that might cause text rendering issues
      if (!currentZoom || currentZoom === 0 || !isFinite(currentZoom)) {
        canvas.setZoom(DEFAULT_ZOOM);

        // Force redraw after zoom correction
        setTimeout(() => {
          dispatch(updateNeedRedrawCanvas(true));
        }, 50);
      }
    }
  }, [canvas, selectedColor, drawingShapes.length, dispatch]);

  return (
    <Design>
      <PageTitle>{t('DESIGN_PAGE.TITLE')}</PageTitle>
      <PageSubtitle>{t('DESIGN_PAGE.SUBTITLE')}</PageSubtitle>
      <div ref={ref}>
        <DrawingArea canvasWidth={width || 800} />
      </div>
    </Design>
  );
};

export default DesignPage;
